import { FunctionComponent, useCallback } from "react";

import { useTranslation } from "react-i18next";

import { AssignStatus } from "@/enum/list.enum";

interface ListPersonStatusProps {
	status: AssignStatus;
	/** Pill size. `sm` is a compact variant (e.g. dense list rows); defaults to `md`. */
	size?: "sm" | "md";
}

const SIZE_CLASSES: Record<NonNullable<ListPersonStatusProps["size"]>, string> = {
	// Compact uppercase pill (unified-prototype v33 `lp-list-status`)
	sm: "px-2 py-0.5 rounded-full text-xxs font-bold uppercase tracking-wide",
	md: "px-2.5 py-1.5 rounded-xl text-ssm",
};

const STATUS_STYLE: Record<string, { bg: string; text: string }> = {
	[AssignStatus.DRAFT]: { bg: "bg-status-info-light", text: "text-status-info" },
	[AssignStatus.SAVED_FOR_LATER]: { bg: "bg-status-warning-light", text: "text-status-warning" },
	[AssignStatus.ASSIGNING]: { bg: "bg-light-blue", text: "text-brand-light" },
	[AssignStatus.ASSIGNED]: { bg: "bg-light-blue", text: "text-brand-light" },
	[AssignStatus.RUNNING]: { bg: "bg-status-running-light", text: "text-status-running" },
	[AssignStatus.BLACKLIST]: { bg: "bg-status-error-light", text: "text-status-error" },
	[AssignStatus.NOT_NOW]: { bg: "bg-status-warning-light", text: "text-status-warning" },
	[AssignStatus.NO_REPLY]: { bg: "bg-status-warning-light", text: "text-status-warning" },
	[AssignStatus.REPLIES]: { bg: "bg-status-success-light", text: "text-status-success" },
	[AssignStatus.TO_BE_NURTURED]: { bg: "bg-status-running-light", text: "text-status-running" },
	[AssignStatus.DO_NOT_CONTACT]: { bg: "bg-status-error-light", text: "text-status-error" },
	[AssignStatus.ENDED]: { bg: "bg-status-info-light", text: "text-status-info" },
	[AssignStatus.PAUSED]: { bg: "bg-status-warning-light", text: "text-status-warning" },
	[AssignStatus.MESSAGE_SENT]: { bg: "bg-status-success-light", text: "text-status-success" },
	[AssignStatus.ARCHIVED]: { bg: "bg-status-info-light", text: "text-status-info" },
	[AssignStatus.FOLLOW_UP]: { bg: "bg-status-running-light", text: "text-status-running" },
	[AssignStatus.DONE]: { bg: "bg-status-success-light", text: "text-status-success" },
	[AssignStatus.UNDER_REVIEW]: { bg: "bg-status-warning-light", text: "text-status-warning" },
	[AssignStatus.TRACKED_PROFILE]: { bg: "bg-light-blue", text: "text-brand-light" },
};

export const ListPersonStatus: FunctionComponent<ListPersonStatusProps> = ({
	status,
	size = "md",
}) => {
	const { t } = useTranslation();
	const ts = useCallback((key: string) => t(`inbox.${key}`), [t]);

	const style = STATUS_STYLE[status];

	if (!style) {
		return <></>;
	}

	return (
		<div className={`${SIZE_CLASSES[size]} ${style.bg} ${style.text} flex flex-row items-center`}>
			{ts(`assignStatus.${status}`)}
		</div>
	);
};
